/**
 * healthService.js — Apple HealthKit / Google Health Connect integration layer.
 *
 * Single point of entry for all health-platform reads and writes. Auto-detects
 * native vs web. On web, every read returns empty / every write is a no-op so
 * the UI can render the "Connect Apple Health" CTA without crashing.
 *
 * Architecture:
 *   - Read on app foreground (cheap, opportunistic, no background scheduling).
 *   - Backend dedup is on raw_healthkit_id, so re-syncing is idempotent.
 *   - User defines what counts (brand axiom): we never sync without explicit
 *     per-category permission. Pacing-engine reads body metrics; gameplay
 *     reads workouts only. We separate the asks.
 *
 * Plugin: @capgo/capacitor-health@8.x (Capacitor 8, Node 22+).
 *
 * Known limitation: @capgo/capacitor-health does not yet expose a
 * `saveWorkout` for full session writes — only `saveSample` for metric writes.
 * Closed-loop workout write-back is queued for V2 (either custom Swift bridge
 * or migration to Flomentum's extended plugin).
 */

import { Capacitor } from '@capacitor/core';

let HealthPlugin = null;
const isNative = Capacitor.isNativePlatform();

// Lazy-load native plugin only on iOS/Android so web build doesn't break.
if (isNative) {
  try {
    // eslint-disable-next-line global-require
    const mod = require('@capgo/capacitor-health');
    HealthPlugin = mod.CapacitorHealth || mod.default || mod;
  } catch (e) {
    // Plugin not bundled in this build — degrade gracefully.
    HealthPlugin = null;
  }
}

// ----- Data category keys (mirror plugin's expected strings) -----
// We separate reads (body data) from writes (Enferno workouts) so the user
// can grant one without the other.
export const HEALTH_READ_CATEGORIES = [
  'workouts',
  'heart-rate',
  'resting-heart-rate',
  'heart-rate-variability',
  'active-energy',
  'steps',
  'sleep',
  'weight',
];

export const HEALTH_WRITE_CATEGORIES = [
  // V1 = metric-only writes (energy + heart-rate samples from logged sessions).
  // Full workout writes land in V2.
  'active-energy',
  'heart-rate',
];

/** Returns true when running inside iOS or Android (Capacitor native shell). */
export const isHealthAvailable = () => isNative && !!HealthPlugin;

/**
 * Request HealthKit / Health Connect permission for the given category lists.
 *
 * Critical iOS detail: HealthKit returns "granted" even when the user denies,
 * to avoid leaking which permissions were rejected (privacy by design). We
 * can't detect denial reliably — we infer it from read attempts returning
 * empty arrays. So we always store the *request timestamp* and treat a
 * successful empty read after the request as "user said no, respect it."
 */
export const requestHealthPermissions = async ({
  read = HEALTH_READ_CATEGORIES,
  write = HEALTH_WRITE_CATEGORIES,
} = {}) => {
  if (!isHealthAvailable()) {
    return { granted: false, reason: 'unavailable' };
  }
  try {
    await HealthPlugin.requestHealthPermissions({ permissions: [...read, ...write] });
    return { granted: true, requestedAt: new Date().toISOString() };
  } catch (e) {
    return { granted: false, reason: e?.message || 'permission-error' };
  }
};

/**
 * Query workouts in a date range. Returns the plugin's normalized format:
 * [{ startDate, endDate, duration, distance, calories, sourceName, uuid, workoutActivityType }, ...]
 *
 * Use `uuid` as the dedup key when syncing to backend (`raw_healthkit_id`).
 */
export const queryWorkouts = async ({ startISO, endISO }) => {
  if (!isHealthAvailable()) return [];
  try {
    const result = await HealthPlugin.queryWorkouts({
      startDate: startISO,
      endDate: endISO,
    });
    return Array.isArray(result?.workouts) ? result.workouts : [];
  } catch {
    return [];
  }
};

/**
 * Query a single metric (steps, heart rate, weight, etc.) for a date range.
 * Returns either a single aggregated value or an array of samples depending on
 * plugin behaviour. Caller normalizes.
 */
export const queryMetric = async ({ metric, startISO, endISO }) => {
  if (!isHealthAvailable()) return null;
  try {
    return await HealthPlugin.queryHealthData({
      dataType: metric,
      startDate: startISO,
      endDate: endISO,
    });
  } catch {
    return null;
  }
};

/**
 * Foreground sync — call on app resume + Workout page open. Returns the
 * subset of workouts not yet seen by the backend (caller decides whether to
 * POST them to /api/health/workouts/sync).
 *
 * Window: last 14 days. Covers gap-filling for users who installed the app
 * after some training history existed.
 */
export const foregroundSyncWindow = async () => {
  if (!isHealthAvailable()) return { workouts: [], ranAt: null };
  const end = new Date();
  const start = new Date(end.getTime() - 14 * 24 * 60 * 60 * 1000);
  const workouts = await queryWorkouts({
    startISO: start.toISOString(),
    endISO: end.toISOString(),
  });
  return { workouts, ranAt: new Date().toISOString() };
};

// ------------------------------------------------------------
// Body-metrics snapshot for Pacing v2
// ------------------------------------------------------------
// Pulls today's HRV / resting HR / sleep and a 7-day trailing average so the
// backend pacing engine can compute a daily capacity score. All fields are
// optional — partial reads still produce a useful score.

const _average = (samples) => {
  if (!Array.isArray(samples) || samples.length === 0) return null;
  const nums = samples
    .map((s) => (typeof s === 'number' ? s : (s?.value ?? s?.quantity)))
    .filter((n) => typeof n === 'number' && !Number.isNaN(n));
  if (nums.length === 0) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
};

const _samplesArray = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.samples)) return raw.samples;
  if (Array.isArray(raw.values)) return raw.values;
  if (typeof raw.value === 'number') return [raw.value];
  return [];
};

/**
 * Build today's body-metrics snapshot for the pacing engine. Returns an
 * object the backend's /api/health/metrics/sync endpoint accepts.
 *
 * Returns null when the plugin isn't available so the caller can short-circuit.
 */
export const collectBodyMetricsSnapshot = async () => {
  if (!isHealthAvailable()) return null;
  const now = new Date();
  const oneDayMs = 24 * 60 * 60 * 1000;
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);
  const sevenDaysAgo = new Date(now.getTime() - 7 * oneDayMs);

  const [hrvToday, hrv7d, rhrToday, rhr7d, sleep] = await Promise.all([
    queryMetric({ metric: 'heart-rate-variability', startISO: todayStart.toISOString(), endISO: now.toISOString() }),
    queryMetric({ metric: 'heart-rate-variability', startISO: sevenDaysAgo.toISOString(), endISO: now.toISOString() }),
    queryMetric({ metric: 'resting-heart-rate', startISO: todayStart.toISOString(), endISO: now.toISOString() }),
    queryMetric({ metric: 'resting-heart-rate', startISO: sevenDaysAgo.toISOString(), endISO: now.toISOString() }),
    queryMetric({ metric: 'sleep', startISO: new Date(now.getTime() - oneDayMs).toISOString(), endISO: now.toISOString() }),
  ]);

  // Sleep returns in-bed minutes (or seconds depending on the plugin).
  // Plugin docs use minutes; we convert to hours, defaulting if unparseable.
  let sleepHours = null;
  const sleepSamples = _samplesArray(sleep);
  if (sleepSamples.length > 0) {
    const totalMinutes = sleepSamples.reduce((acc, s) => {
      const dur = typeof s === 'number' ? s : (s?.duration ?? s?.value ?? 0);
      return acc + (typeof dur === 'number' ? dur : 0);
    }, 0);
    sleepHours = totalMinutes / 60;
    // If total is implausibly large, plugin probably returned seconds.
    if (sleepHours > 24) sleepHours = totalMinutes / 3600;
  }

  return {
    sample_date: todayStart.toISOString().slice(0, 10),
    hrv_ms_today: _average(_samplesArray(hrvToday)),
    hrv_ms_7d_avg: _average(_samplesArray(hrv7d)),
    resting_hr_bpm_today: _average(_samplesArray(rhrToday)),
    resting_hr_bpm_7d_avg: _average(_samplesArray(rhr7d)),
    sleep_hours_last_night: sleepHours,
  };
};

// ------------------------------------------------------------
// Workout write-back (V2) — re-exported from enfernoHealthWorkouts.js
// ------------------------------------------------------------
// Lives in its own file because it's powered by a separate custom Capacitor
// plugin (the Capgo plugin doesn't expose saveWorkout). Re-exported here so
// every health-platform interaction has one entry point.
export { saveWorkoutToHealth, isHealthWriteAvailable } from './enfernoHealthWorkouts';
